import { useEffect, useState } from "react";

import {
  ArrowButton,
  Button,
  DatePicker,
  Label,
  Table,
  TableMetadata,
  useTable,
  Typography,
  ColumnSortEnum,
  TextField,
  Select,
  TableStateWithFilters,
  IconWrapper,
  AutocompleteMultipleSelect
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ReactComponent as FolderAddIcon } from "@king-ict/eupisi-ui/components/Icon/FolderAdd.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, ExportTypeEnum, PagedResult } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  searchMunicipalities, searchInstitutionsCodeBook, searchProgramRequestsMinistry,
  searchProgramRequestsAgency, exportProgramRequestAgency, exportProgramRequestMinistry,
  SearchProgramRequestsMinistryRequest, searchEducationSectors
} from "../../api/nisooApi";
import { useDidUpdateEffect, useInput } from "../../hooks";
import { InstitutionCodeBook } from "../../models/Institution";
import { MunicipalityCodeBook } from "../../models/Municipality";
import {
  ProgramRequestListItem,
  ProgramRequestMinistrySearch,
  ProgramRequestMinistrySearchValidationScheme,
  ProgramRequestSort, PreviousPage
} from "../../models/ProgramRequest";
import { SortDirection } from "../../models/Shared/SortDirection";
import { RoleEnum } from "../../models/User/RoleEnum";
import { useGetCountiesQuery, useGetProgramRequestStatusesByUserQuery, useGetProgramTypesQuery } from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { programRequestDetailsUrl } from "../../routes/routes";
import { getProgramRequestStatusColor } from "../../utils/programRequestUtils";
import styles from "./ProgramRequestList.module.scss";

export function ProgramRequestList() {
  const storageKey = 'ProgramRequestListSearchTableState';
  const createInitialTableState = (): TableStateWithFilters<ProgramRequestMinistrySearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        countyIds: [],
        institutions: [] as InstitutionCodeBook[],
        municipalities: [] as MunicipalityCodeBook[],
        educationSectors: [] as CodeBook[],
        programTypeIds: [],
        programName: "",
        programRequestStatusIds: [],
        dateFrom: undefined,
        dateTo: undefined,
        classificationMark: "",
        page: 1,
        pageSize: 10,
        sortBy: ProgramRequestSort.ProgramRequestDate,
        order: ColumnSortEnum.DESC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<ProgramRequestMinistrySearch>;
    //JSON parse cannot parse dates - it reads the dates as string - so we do it manually
    if (result.dateFrom) {
      result.dateFrom = new Date(result.dateFrom.toString());
    }
    if (result.dateTo) {
      result.dateTo = new Date(result.dateTo.toString());
    }
    return result;
  }

  const createInitialProgramRequestSearch = (initialTableState: TableStateWithFilters<ProgramRequestMinistrySearch>): ProgramRequestMinistrySearch => {
    return {
      countyIds: initialTableState.countyIds,
      institutions: initialTableState.institutions,
      municipalities: initialTableState.municipalities,
      educationSectors: initialTableState.educationSectors,
      programTypeIds: initialTableState.programTypeIds,
      programName: initialTableState.programName,
      programRequestStatusIds: initialTableState.programRequestStatusIds,
      dateFrom: initialTableState.dateFrom,
      dateTo: initialTableState.dateTo,
      classificationMark: initialTableState.classificationMark
    }
  }

  const createInitialProgramRequests = (): PagedResult<ProgramRequestListItem> => {
    return {
      currentPage: 1,
      firstRowOnPage: 0,
      lastRowOnPage: 0,
      pageCount: 0,
      pageSize: 10,
      rowCount: 0,
      results: [],
    }
  }

  const convertProgramRequestSearchToApiRequest = (programRequestSearch: ProgramRequestMinistrySearch) => {
    var request = {
      countyIds: programRequestSearch.countyIds,
      institutionIds: programRequestSearch.institutions.map(ins => ins.id),
      municipalityIds: programRequestSearch.municipalities.map(mun => mun.id),
      educationSectorIds: programRequestSearch.educationSectors.map(es => es.id),
      programName: programRequestSearch.programName,
      programRequestStatusIds: programRequestSearch.programRequestStatusIds,
      programTypeIds: programRequestSearch.programTypeIds,
      dateFrom: programRequestSearch.dateFrom,
      dateTo: programRequestSearch.dateTo,
      classificationMark: programRequestSearch.classificationMark,
      sortBy: tableState.sortBy ?? ProgramRequestSort.ProgramRequestDate,
      sortDirection: tableState.order && tableState.order === ColumnSortEnum.ASC ? SortDirection.Asc : SortDirection.Desc,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    };
    return request;
  }

  const [initialTableState] = useState<TableStateWithFilters<ProgramRequestMinistrySearch>>(() => createInitialTableState());
  const [userHasProgramRequests, setUserHasProgramRequests] = useState<boolean>(false);
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<MunicipalityCodeBook[]>([] as MunicipalityCodeBook[]);
  const [filteredInsitutions, setFilteredInsitutions] = useState<InstitutionCodeBook[]>([] as InstitutionCodeBook[]);
  const [programRequests, setProgramRequests] = useState<PagedResult<ProgramRequestListItem>>(() => createInitialProgramRequests());
  const [programRequestSearch, setProgramRequestSearch] = useState<ProgramRequestMinistrySearch>(() => createInitialProgramRequestSearch(initialTableState));
  const [lastUsedSearch, setLastUsedSearch] = useState<SearchProgramRequestsMinistryRequest>();
  const [filteredEducationSectors, setFilteredEducationSectors] = useState<CodeBook[]>([] as CodeBook[]);

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: counties } = useGetCountiesQuery();
  const { data: userSession } = useGetUserSessionQuery();
  const { data: programRequestStatuses } = useGetProgramRequestStatusesByUserQuery(userSession?.roleId ?? 0);

  const [errors, doValidations] = useValidation({
    data: programRequestSearch,
    validationSchema: ProgramRequestMinistrySearchValidationScheme,
  });

  const programRequestTableMetadata: TableMetadata<ProgramRequestListItem> = {
    rowKey: "programRequestId",
    columns: [
      {
        columnKey: "programRequestDate",
        header: t("common.programRequestDate"),
        accessor: (item: ProgramRequestListItem) =>
          hrDate(item.programRequestDate) + ' ' +
          item.programRequestDate.getHours().toString().padStart(2, '0') + ':' +
          item.programRequestDate.getMinutes().toString().padStart(2, '0'),
        sortBy: ProgramRequestSort.ProgramRequestDate
      },
      {
        columnKey: "institutionName",
        header: t("common.institution"),
        accessor: (item: ProgramRequestListItem) => item.institutionName + ', ' + item.institutionTownName,
        color: "success",
        sortBy: ProgramRequestSort.InstitutionName
      },
      {
        columnKey: "programName",
        header: t("common.programName"),
        accessor: "programName",
        sortBy: ProgramRequestSort.ProgramName
      },
      {
        columnKey: "classificationMark",
        header: t("common.classificationMark"),
        accessor: "classificationMark",
        sortBy: ProgramRequestSort.ClassificationMark
      },
      {
        columnKey: "educationSector",
        header: t("common.educationSector"),
        accessor: "educationSectorName",
        sortBy: ProgramRequestSort.EducationSector
      },
      {
        columnKey: "programTypeName",
        header: t("common.programType"),
        accessor: "programTypeName",
        sortBy: ProgramRequestSort.ProgramTypeName
      },
      {
        columnKey: "programRequestStatusName",
        header: t("common.status"),
        accessor: (item) => {
          return (
            <div className={styles.statusColumn}>
              <Label color={getProgramRequestStatusColor(item.programRequestStatusId)}>
                {item.programRequestStatusName}
              </Label>
            </div>
          );
        },
        sortBy: ProgramRequestSort.ProgramRequestStatusMinistry
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("request.goToRequestDetails")}
              onClick={() => onOpenRequestDetails(item.programRequestId)}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  const onOpenRequestDetails = (requestId: number) => {
    // disabled for testing    
    history.push(programRequestDetailsUrl(requestId), { previousPage: PreviousPage.ProgramRequestListPage });
  };

  var [tableProps, tableState, setFilters] = useTable<ProgramRequestMinistrySearch>(
    {
      metadata: programRequestTableMetadata,
      data: programRequests.results,
      pagination: true,
      paginationPage: initialTableState.page,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: programRequests.rowCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  useEffect(() => {
    if (!userSession || (userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.ASOO
      && userSession.roleId !== RoleEnum.Helpdesk && userSession.roleId !== RoleEnum.ASOOAdmin && userSession.roleId !== RoleEnum.MZOMAdmin
      && userSession.roleId !== RoleEnum.ASOOOffice && userSession.roleId !== RoleEnum.MZOMOffice)) {
      return;
    }

    const checkIfUserHasProgramRequests = async () => {
      var request = {
        countyIds: [],
        municipalityIds: [],
        institutionIds: [],
        educationSectorIds: [],
        programTypeIds: [],
        programName: '',
        programRequestStatusIds: [],
        dateFrom: undefined,
        dateTo: undefined,
        classificationMark: '',
        sortBy: ProgramRequestSort.ProgramRequestDate,
        sortDirection: SortDirection.Asc,
        page: 1,
        pageSize: 1
      };

      var results = null;
      if (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.MZOMOffice || userSession.roleId === RoleEnum.Helpdesk) {
        results = await searchProgramRequestsMinistry(request);
      }
      else {
        results = await searchProgramRequestsAgency(request);
      }

      if (results.rowCount > 0) {
        setUserHasProgramRequests(true);
      }
    }
    checkIfUserHasProgramRequests();
  }, [userSession]);

  useEffect(() => {
    if (!userSession || (userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.ASOO
      && userSession.roleId !== RoleEnum.Helpdesk && userSession.roleId !== RoleEnum.ASOOAdmin && userSession.roleId !== RoleEnum.MZOMAdmin
      && userSession.roleId !== RoleEnum.ASOOOffice && userSession.roleId !== RoleEnum.MZOMOffice)) {
      return;
    }

    const searchProgramRequests = async () => {
      const request = convertProgramRequestSearchToApiRequest(programRequestSearch)
      setLastUsedSearch(request);

      var results = null;
      if (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.MZOMOffice || userSession.roleId === RoleEnum.Helpdesk) {
        results = await searchProgramRequestsMinistry(request);
      }
      else {
        results = await searchProgramRequestsAgency(request);
      }
      setProgramRequests(results);
    }
    searchProgramRequests();
    // programRequestSearch change shouldn't trigger this fetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, userSession]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...programRequestSearch,
      sortBy: tableState.sortBy ?? ProgramRequestSort.ProgramRequestDate,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? 10
    }));
  }, [tableState])

  const handleMunicipalityFilterChange = async (filter: string) => {
    var result = await searchMunicipalities(filter, programRequestSearch.countyIds);
    setFilteredMunicipalities(result);
  }

  const handleCountyChange = (e: any) => {
    const newCountyIds = e.target.value as number[];

    let newMunicipalities = programRequestSearch.municipalities.slice();
    newMunicipalities = newMunicipalities.filter((mun) => newCountyIds.includes(mun.countyId));
    let newInstitutions = programRequestSearch.institutions.slice();
    newInstitutions = newInstitutions.filter((institution) => newCountyIds.includes(institution.countyId));

    const newProgramRequestSearch: ProgramRequestMinistrySearch = {
      ...programRequestSearch,
      countyIds: newCountyIds,
      institutions: newInstitutions,
      municipalities: newMunicipalities
    };
    setProgramRequestSearch(newProgramRequestSearch);
  }

  const handleMunicipalityChange = (e: any) => {
    const newMunicipalities = e.target.value as MunicipalityCodeBook[];
    const newMunicipalityIds = newMunicipalities.map((mun) => mun.id);

    let newInstitutions = programRequestSearch.institutions.slice();
    newInstitutions = newInstitutions.filter((ins) => newMunicipalityIds.includes(ins.municipalityId));

    const newProgramRequestSearch: ProgramRequestMinistrySearch = {
      ...programRequestSearch,
      municipalities: newMunicipalities.slice(0, 10),
      institutions: newInstitutions
    };
    setProgramRequestSearch(newProgramRequestSearch);
  }

  const handleInstitutionFilterChange = async (filter: string) => {
    var result = await searchInstitutionsCodeBook(filter, programRequestSearch.municipalities?.map((mun) => mun.id), programRequestSearch.countyIds);
    setFilteredInsitutions(result);
  }

  const handleEducationSectorFilterChange = async (filter: string) => {
    var result = await searchEducationSectors(filter);
    setFilteredEducationSectors(result);
  }

  const handleInstitutionChange = (e: any) => {
    const newInstitutions = e.target.value as InstitutionCodeBook[];
    const newProgramRequestSearch: ProgramRequestMinistrySearch = {
      ...programRequestSearch,
      institutions: newInstitutions.slice(0, 10)
    };
    setProgramRequestSearch(newProgramRequestSearch);
  }

  const handleEducationSectorChange = (e: any) => {
    const newEducationSectors = e.target.value as CodeBook[];
    const newProgramRequestSearch: ProgramRequestMinistrySearch = {
      ...programRequestSearch,
      educationSectors: newEducationSectors.slice(0, 10)
    };
    setProgramRequestSearch(newProgramRequestSearch);
  }

  const clearFilters = () => {
    var emptySearch = {
      countyIds: [],
      municipalities: [],
      institutions: [],
      educationSectors: [],
      programTypeIds: [],
      programName: "",
      programRequestStatusIds: [],
      dateFrom: undefined,
      dateTo: undefined,
      classificationMark: ""
    };
    setProgramRequestSearch(emptySearch);
    setFilters(emptySearch)
  }

  const search = async () => {
    const isValid = doValidations();
    if (!isValid) {
      return;
    }
    setFilters(programRequestSearch);
  };

  const download = async (exportType: ExportTypeEnum) => {
    if (!lastUsedSearch || !userSession) {
      return;
    }

    const request = {
      countyIds: lastUsedSearch.countyIds,
      institutionIds: lastUsedSearch.institutionIds,
      municipalityIds: lastUsedSearch.municipalityIds,
      educationSectorIds: lastUsedSearch.educationSectorIds,
      programTypeIds: lastUsedSearch.programTypeIds,
      programName: lastUsedSearch.programName,
      dateFrom: lastUsedSearch.dateFrom,
      dateTo: lastUsedSearch.dateTo,
      classificationMark: lastUsedSearch.classificationMark,
      programRequestStatusIds: lastUsedSearch.programRequestStatusIds,
      sortBy: lastUsedSearch.sortBy,
      sortDirection: lastUsedSearch.sortDirection,
      exportType: exportType
    }

    if (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.MZOMOffice || userSession.roleId === RoleEnum.Helpdesk) {
      await exportProgramRequestMinistry(request);
    }
    else {
      await exportProgramRequestAgency(request);
    }
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <h2>{t("navigation.programRequestList")}</h2>
        </Col>
      </Row>
      {userHasProgramRequests &&
        <>
          <Row className="mt-4">
            <Col md={6}>
              <Select
                multiple
                label={t("common.county")}
                name={nameof<typeof programRequestSearch>("countyIds")}
                value={programRequestSearch.countyIds}
                onChange={handleCountyChange}
                selectData={codeBookToSelectData(counties)}
              />
            </Col>
            <Col md={6}>
              <AutocompleteMultipleSelect<MunicipalityCodeBook>
                label={t("common.municipality")}
                name={nameof<typeof programRequestSearch>("municipalities")}
                value={programRequestSearch.municipalities}
                valueProp="id"
                textProp="name"
                onChange={handleMunicipalityChange}
                selectOptions={filteredMunicipalities}
                onFilterChange={handleMunicipalityFilterChange}
                maximumSelectedItems={10}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <AutocompleteMultipleSelect<CodeBook>
                label={t("common.institution")}
                name={nameof<typeof programRequestSearch>("institutions")}
                value={programRequestSearch.institutions}
                valueProp="id"
                textProp="name"
                onChange={handleInstitutionChange}
                selectOptions={filteredInsitutions}
                onFilterChange={handleInstitutionFilterChange}
                maximumSelectedItems={10}
              />
            </Col>
            <Col md={6}>
              <Select
                multiple
                label={t("request.programType")}
                name={nameof<typeof programRequestSearch>("programTypeIds")}
                value={programRequestSearch.programTypeIds}
                onChange={handleChange(setProgramRequestSearch)}
                selectData={codeBookToSelectData(programTypes)}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <TextField
                label={t("request.classificationMark")}
                name={nameof<typeof programRequestSearch>("classificationMark")}
                value={programRequestSearch.classificationMark}
                onChange={handleChange(setProgramRequestSearch)}
              />
            </Col>
            <Col md={6}>
              <AutocompleteMultipleSelect<CodeBook>
                label={t("common.educationSector")}
                value={programRequestSearch.educationSectors}
                valueProp="id"
                textProp="name"
                onChange={handleEducationSectorChange}
                selectOptions={filteredEducationSectors}
                onFilterChange={handleEducationSectorFilterChange}
                maximumSelectedItems={10}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <TextField
                label={t("common.programName")}
                name={nameof<typeof programRequestSearch>("programName")}
                value={programRequestSearch.programName}
                onChange={handleChange(setProgramRequestSearch)}
              />
            </Col>
            <Col md={3}>
              <DatePicker
                value={programRequestSearch.dateFrom}
                label={t("common.dateFrom")}
                name={nameof<typeof programRequestSearch>("dateFrom")}
                onChange={(e) => {
                  if (e.target.value) {
                    let newDate = (e.target.value as Date);
                    newDate.setHours(0);
                    newDate.setMinutes(0);
                    newDate.setSeconds(0);
                    newDate.setMilliseconds(0);
                  }
                  handleChange(setProgramRequestSearch)(e);
                }}
                errorText={errors?.dateFrom}
                isClearable={true}
                minYear={new Date().getFullYear() - 25}
                maxYear={new Date().getFullYear() + 25}
              />
            </Col>
            <Col md={3}>
              <DatePicker
                value={programRequestSearch.dateTo}
                label={t("common.dateTo")}
                name={nameof<typeof programRequestSearch>("dateTo")}
                onChange={(e) => {
                  if (e.target.value) {
                    let newDate = (e.target.value as Date);
                    newDate.setHours(23);
                    newDate.setMinutes(59);
                    newDate.setSeconds(59);
                    newDate.setMilliseconds(0);
                  }
                  handleChange(setProgramRequestSearch)(e);
                }}
                isClearable={true}
                minYear={new Date().getFullYear() - 25}
                maxYear={new Date().getFullYear() + 25}
              />
            </Col>
          </Row>
          <Row className="mb-5">
            <Col md={6}>
              <Select
                multiple
                label={t("common.programRequestStatus")}
                name={nameof<typeof programRequestSearch>("programRequestStatusIds")}
                value={programRequestSearch.programRequestStatusIds}
                onChange={handleChange(setProgramRequestSearch)}
                selectData={codeBookToSelectData(programRequestStatuses)}
              />
            </Col>
            <Col md={6} className="d-flex justify-content-end align-items-center">
              <Button onClick={search}>
                {t("common.search")}
              </Button>
              <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={clearFilters}>
                {t("common.clearFilters")}
              </Typography>
            </Col>
          </Row>
          <Table  {...tableProps} />
          <Row className="mt-5">
            <Col md={12} className="d-flex justify-content-end align-items-center">
              <Button
                className={styles.exportButton}
                outline
                color={"primary"}
                icon={<DownloadIcon />}
                onClick={() => download(ExportTypeEnum.Csv)}
              >
                {t("common.downloadDocumentExtension", { extension: ".csv" })}
              </Button>
              <Button
                className={styles.exportButton}
                outline
                color={"primary"}
                icon={<DownloadIcon />}
                onClick={() => download(ExportTypeEnum.Excel)}
              >
                {t("common.downloadDocumentExtension", { extension: ".xslx" })}
              </Button>
            </Col>
          </Row>
        </>
      }
      {!userHasProgramRequests &&
        (<Row>
          <Col md={12} className={styles.emptyList}>
            <IconWrapper color="noted-2">
              <FolderAddIcon aria-label={t("folderAddIcon")} />
            </IconWrapper>
            <Typography variant="largeBold" component="h2">
              {t("requests.noRequests")}
            </Typography>
          </Col>
        </Row>)
      }
    </>
  );
}